import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import axios from 'axios';
import { MapPin, Heart, Send, Users, Calendar, HandHeart } from 'lucide-react';
import UserProfileModal from './UserProfileModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DayViewModal = ({ 
  isOpen, 
  onClose, 
  selectedDate,
  onJoinClick,
}) => {
  const { user } = useAuth();
  const [commitments, setCommitments] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserData, setSelectedUserData] = useState(null);

  useEffect(() => {
    if (isOpen && selectedDate) {
      fetchData();
    }
  }, [isOpen, selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [commitmentsRes, commentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/commitments/date/${selectedDate}`),
        axios.get(`${API_URL}/api/comments/date/${selectedDate}`)
      ]);
      setCommitments(commitmentsRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Failed to fetch day data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setSubmittingComment(true);
    try {
      const response = await axios.post(`${API_URL}/api/comments`, {
        date: selectedDate,
        content: newComment.trim()
      });
      setComments(prev => [...prev, response.data]);
      setNewComment('');
      toast.success('Encouragement shared!');
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleProfileClick = (commitment) => {
    if (!commitment.is_anonymous) {
      setSelectedUserId(commitment.user_id);
      setSelectedUserData({
        displayName: commitment.display_name,
        profilePicture: null // We'll fetch from API
      });
    }
  };

  const formatDisplayDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCommentTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const userAlreadyCommitted = user && commitments.some(c => c.user_id === user.id);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="day-view-modal">
          <DialogHeader>
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Calendar className="h-4 w-4" />
              <span className="text-sm">{formatDisplayDate(selectedDate)}</span>
            </div>
            <DialogTitle className="font-display text-2xl flex items-center gap-3">
              <Users className="h-6 w-6 text-primary" />
              {commitments.length} {commitments.length === 1 ? 'Person' : 'People'} Fasting
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-4 -mr-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
              </div>
            ) : (
              <div className="space-y-6 pb-4">
                {/* Commitments List */}
                {commitments.length === 0 ? (
                  <div className="text-center py-8">
                    <HandHeart className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                    <p className="text-muted-foreground">No one has committed to fast on this day yet.</p>
                    <p className="text-sm text-muted-foreground mt-1">Be the first!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {commitments.map((commitment, index) => (
                      <div 
                        key={commitment.id}
                        className="bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-border/50 animate-fade-in-up"
                        style={{ animationDelay: `${index * 0.1}s` }}
                        data-testid={`commitment-card-${commitment.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {/* Clickable Profile Picture */}
                              <button
                                onClick={() => handleProfileClick(commitment)}
                                disabled={commitment.is_anonymous}
                                className={`w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden transition-all ${
                                  !commitment.is_anonymous ? 'hover:ring-2 hover:ring-primary/30 cursor-pointer' : 'cursor-default'
                                }`}
                                data-testid={`profile-btn-${commitment.id}`}
                              >
                                <span className="text-primary font-medium text-sm">
                                  {commitment.display_name.charAt(0).toUpperCase()}
                                </span>
                              </button>
                              <div>
                                <button
                                  onClick={() => handleProfileClick(commitment)}
                                  disabled={commitment.is_anonymous}
                                  className={`font-medium text-foreground text-left ${
                                    !commitment.is_anonymous ? 'hover:text-primary cursor-pointer' : 'cursor-default'
                                  }`}
                                >
                                  {commitment.display_name}
                                </button>
                                {commitment.location && (
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {commitment.location}
                                  </p>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-foreground/80 mt-2">
                              "{commitment.reason}"
                            </p>
                            {commitment.charity_name && (
                              <p className="text-sm text-primary mt-2 flex items-center gap-1">
                                <Heart className="h-3.5 w-3.5" />
                                Supporting: {commitment.charity_name}
                              </p>
                            )}
                            {commitment.reason_categories.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {commitment.reason_categories.map(cat => (
                                  <span 
                                    key={cat}
                                    className="text-xs bg-secondary px-2 py-0.5 rounded-full text-secondary-foreground"
                                  >
                                    {cat.replace('_', ' ')}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Join CTA */}
                {!userAlreadyCommitted && (
                  <div className="sticky bottom-0 bg-gradient-to-t from-background via-background to-transparent pt-4 pb-2">
                    <Button
                      onClick={onJoinClick}
                      className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-6 text-lg btn-lift"
                      data-testid="day-view-join-btn"
                    >
                      Join This Day
                    </Button>
                  </div>
                )}

                {userAlreadyCommitted && (
                  <div className="bg-sage-50 rounded-lg p-4 text-center">
                    <p className="text-sage-700 font-medium">You're fasting on this day!</p>
                  </div>
                )}

                <Separator className="my-6" />

                {/* Comments Section */}
                <div>
                  <h3 className="font-display text-lg mb-4">Encouragement & Support</h3>
                  
                  {comments.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No messages yet. Be the first to share some encouragement!
                    </p>
                  ) : (
                    <div className="space-y-3 mb-4">
                      {comments.map((comment, index) => (
                        <div 
                          key={comment.id}
                          className="bg-secondary/50 rounded-lg p-3 animate-fade-in"
                          style={{ animationDelay: `${index * 0.05}s` }}
                          data-testid={`comment-${comment.id}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{comment.user_display_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatCommentTime(comment.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-foreground/80">{comment.content}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Comment Form */}
                  {user ? (
                    <form onSubmit={handleSubmitComment} className="flex gap-2">
                      <Input
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Share some encouragement..."
                        className="flex-1"
                        data-testid="comment-input"
                      />
                      <Button
                        type="submit"
                        disabled={submittingComment || !newComment.trim()}
                        size="icon"
                        className="bg-primary hover:bg-primary/90"
                        data-testid="comment-submit"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </form>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center">
                      Log in to share encouragement
                    </p>
                  )}
                </div>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={!!selectedUserId}
        onClose={() => {
          setSelectedUserId(null);
          setSelectedUserData(null);
        }}
        userId={selectedUserId}
        displayName={selectedUserData?.displayName}
        profilePicture={selectedUserData?.profilePicture}
      />
    </>
  );
};

export default DayViewModal;
